/**
 * The account menu's "Full width": pages drop the 1240px cap and use all the space beside the sidebar.
 * One choice for every Hume app, as components' ui.js keeps it in Hire and Manage: localStorage
 * "content-width" here, and the cookie "hume-width" on the apps' shared domain (humehr.app). index.html
 * applies it before first paint as <html data-width="full">; the shell reads that attribute.
 */

export type ContentWidth = "full" | "standard";

const STORAGE_KEY = "content-width";
const COOKIE = "hume-width";
const COOKIE_PATTERN = /(?:^|;\s*)hume-width=(full|standard)\b/;

function cookieWidth(): ContentWidth | null {
  const match = document.cookie.match(COOKIE_PATTERN);
  return match ? (match[1] as ContentWidth) : null;
}

/** The shared cookie (set from any Hume app) first, then this app's own storage. */
export function readContentWidth(): ContentWidth {
  const shared = cookieWidth();
  if (shared) return shared;
  try {
    return localStorage.getItem(STORAGE_KEY) === "full" ? "full" : "standard";
  } catch {
    return "standard";
  }
}

export function applyContentWidth(width: ContentWidth): void {
  if (width === "full") document.documentElement.setAttribute("data-width", "full");
  else document.documentElement.removeAttribute("data-width");
}

/** The widest domains first (humehr.app before learn.humehr.app); the browser refuses a public suffix. */
export function cookieDomains(hostname: string): string[] {
  const labels = hostname.split(".");
  const domains: string[] = [];
  for (let i = Math.max(labels.length - 2, 0); i >= 0; i--) domains.push(labels.slice(i).join("."));
  return domains;
}

function shareCookie(width: ContentWidth): void {
  const rest = `=${width}; path=/; max-age=31536000; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  for (const domain of cookieDomains(location.hostname)) {
    document.cookie = `${COOKIE}${rest}; domain=${domain}`;
    if (cookieWidth() === width) return;
  }
  document.cookie = COOKIE + rest; // localhost
}

export function saveContentWidth(width: ContentWidth): void {
  try {
    localStorage.setItem(STORAGE_KEY, width);
  } catch {
    /* private mode: the cookie still carries it */
  }
  shareCookie(width);
  applyContentWidth(width);
}
